import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { PanelState } from '@racemap/utilities/consts/events';
import { hasPois, hasSplits } from '@racemap/utilities/functions/map';
import { Button, Card, Typography } from 'antd';
import { Col, Row } from 'antd';
import { Link } from 'react-router-dom';
import { MapEditorLink } from 'src/components/BasicComponents';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { BasicPanel } from '../../BasicComponents/BasicPanel/BasicPanel';
import { IconUpload } from '../../BasicComponents/Icons';
import GeojsonDropView from '../../GeojsonDropView';
import MapPreview from '../../MapPreview';

interface Props {
  status: PanelState;
}

const { Title, Text } = Typography;

export const TrackPanel = ({ status }: Props) => {
  const event = useCurrentEvent();
  const successState = status === PanelState.SUCCESS;
  const withSplits = hasSplits(event);
  const withPois = hasPois(event);

  if (event == null) return null;

  if (successState)
    return (
      <Card>
        <Row justify="space-between" align="top" gutter={16} wrap={false}>
          <Col flex="auto">
            <Title level={3} style={{ marginTop: 0 }}>
              Customize your map
            </Title>
            <ul style={{ fontSize: 'small', marginTop: '5px' }}>
              {!withPois && (
                <li>
                  <Text>Set points of interest</Text>
                </li>
              )}
              {!withSplits && (
                <li>
                  <Text>Set splits for timekeeping</Text>
                </li>
              )}
            </ul>
            <Text>
              Go the the <MapEditorLink eventId={event.id} /> to customize your map.
            </Text>
          </Col>
          <Col flex="none">
            <IconButton style={{ width: '128px', height: '128px' }}>
              <MapPreview event={event} width={128} height={128} />
            </IconButton>
          </Col>
        </Row>
      </Card>
    );

  return (
    <BasicPanel title="Add tracks, POIs and Splits" defaultOpen={!successState}>
      <Row style={{ display: 'flex', justifyContent: 'center' }}>
        <Col md={12} style={{ textAlign: 'center' }}>
          <Subtitle>Upload your track</Subtitle>
          <TrackDropContainer>
            <UploadButton>
              <GeojsonDropView
                event={event}
                dropViewClass="track-dropview"
                withDropTargetClick={true}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <IconUpload />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      fontSize: 'small',
                    }}
                  >
                    *.gpx, *.kml, *.geojson
                  </div>
                </div>
              </GeojsonDropView>
            </UploadButton>
          </TrackDropContainer>
        </Col>
        <Col md={12} style={{ textAlign: 'center' }}>
          <Link to="../map">
            <Subtitle>Draw your track</Subtitle>
            <IconButton>
              <MapPreview event={event} height={128} width={700} />
            </IconButton>
          </Link>
        </Col>
      </Row>
    </BasicPanel>
  );
};

const TrackDropContainer = styled.div`
  margin-bottom: 10px;

  .drop-target {
    height: 128px;
    margin-right: 10px;
    box-sizing: content-box;
    border: 3px dashed #ddd;

    display: flex;
    align-items: center;
    justify-content: center;

    .upload-button {
      color: #999;
      font-size: 50px;
      cursor: pointer;
    }

    .upload-button:hover {
      color: #555;
    }
  }
  &.file-hover .drop-target {
    border: 3px solid lightgreen;
  }
`;

const UploadButton = styled.div`
  color: #999;
  font-size: 50px;
  cursor: pointer;
  :hover {
    color: #555;
  }
`;

const Subtitle = styled.div`
  font-size: large;
  color: ${RacemapColors.DarkGray};
  margin: 10px;
`;

const IconButton = styled(Button)`
  width: 100%;
  height: 134px;
  overflow: hidden;
  position: relative;
  padding: 0px;
  background-color: #fff;
  border: 3px solid ${RacemapColors.LightGray};
  border-radius: 10px;
  :hover {
    background-color: #fff;
    border: 3px solid ${RacemapColors.LightGray};
  }
`;
